import React, { useEffect, useState } from "react";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
    TextField,
    Button,
    Paper,
    Box,
    Typography,
    Grid,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Alert,
} from "@mui/material";
import useTransformarIniciales from "../../hooks/useTransformarIniciales";

const validarDNI = (dni) => {
    if (!/^[0-9]{8}[A-Z]$/.test(dni)) return false;
    const letras = "TRWAGMYFPDXBNJZSQVHLCKE";
    const numero = parseInt(dni.substring(0, 8), 10);
    const letraEsperada = letras[numero % 23];
    return dni.charAt(8) === letraEsperada;
};

const ColegiadoAdminForm = ({ initialValues, modo, onSubmit }) => {
    const [catalogos, setCatalogos] = useState({});
    const [mensaje, setMensaje] = useState(null);
    const [originalesTransformados, setOriginalesTransformados] = useState({});

    const campos = {
        nombre: "Nombre",
        apellido_1: "Primer Apellido",
        apellido_2: "Segundo Apellido",
        nif: "NIF",
        fecha_Nacimiento: "Fecha de Nacimiento",
        sexo: "Sexo",
        direccion: "Dirección",
        localidad: "Localidad",
        c_p_: "Código Postal",
        provincia: "Provincia",
        pais: "País",
        nacionalidad: "Nacionalidad",
        paisNacimientoId: "País de Nacimiento",
        telefono_1: "Teléfono 1",
        telefono_2: "Teléfono 2",
        email: "Correo Electrónico",
        email_2: "Correo Electrónico Secundario",
        iban: "IBAN",
        lugaresTrabajoIds: "Lugar de Trabajo",
        situacionProfesionalId: "Situación Profesional",
        especialidadesIds: "Especialidades",
        estadoId: "Estado",
        titulacionPrincipalId: "Titulación Principal",
        tipoPagoId: "Tipo de Pago",
    };

    useEffect(() => {
        const fetchCatalogos = async () => {
            try {
                const [
                    nacionalidades,
                    paises,
                    lugaresTrabajo,
                    estados,
                    situaciones,
                    tiposPago,
                    titulaciones,
                    especialidades,
                ] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/paises`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/paises`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/lugares-trabajo`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/estados`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/situaciones-profesionales`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/tipos-pago`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/titulaciones-principales`),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/especialidades`),
                ]);

                setCatalogos({
                    nacionalidades: nacionalidades.data,
                    paises: paises.data,
                    estados: estados.data,
                    situaciones: situaciones.data,
                    tiposPago: tiposPago.data,
                    titulaciones: titulaciones.data,
                    especialidades: especialidades.data,
                    lugaresTrabajo: lugaresTrabajo.data,
                    sexo: [
                        { id: "Hombre", nombre: "Hombre" },
                        { id: "Mujer", nombre: "Mujer" },
                    ],
                });
            } catch (err) {
                console.error("Error cargando catálogos:", err);
            }
        };

        fetchCatalogos();
    }, []);

    const valoresTransformados = useTransformarIniciales(initialValues);

    useEffect(() => {
        setOriginalesTransformados(valoresTransformados);
    }, [initialValues]);

    const camposSelect = {
        estadoId: { catalogo: catalogos.estados, multiple: false },
        titulacionPrincipalId: { catalogo: catalogos.titulaciones, multiple: false },
        situacionProfesionalId: { catalogo: catalogos.situaciones, multiple: false },
        tipoPagoId: { catalogo: catalogos.tiposPago, multiple: false },
        paisNacimientoId: { catalogo: catalogos.paises, multiple: false },
        nacionalidad: { catalogo: catalogos.nacionalidades, multiple: false },
        pais: { catalogo: catalogos.paises, multiple: false },
        sexo: { catalogo: catalogos.sexo, multiple: false },
        especialidadesIds: { catalogo: catalogos.especialidades, multiple: true },
        lugaresTrabajoIds: { catalogo: catalogos.lugaresTrabajo, multiple: true },
    };

    const esCampoSelect = (campo) => Object.keys(camposSelect).includes(campo);
    const obtenerOpciones = (campo) => camposSelect[campo]?.catalogo || [];
    const esMultiple = (campo) => camposSelect[campo]?.multiple || false;

    const secciones = {
        "Datos Personales": [
            "nombre", "apellido_1", "apellido_2", "nif", "fecha_Nacimiento", "sexo",
            "nacionalidad",
            "paisNacimientoId"
        ],
        "Dirección": [
            "direccion", "localidad", "c_p_", "provincia", "pais"
        ],
        "Datos de Contacto": [
            "telefono_1", "telefono_2", "email", "email_2"
        ],
        "Datos Bancarios": ["iban", "tipoPagoId"],
        "Datos Académicos": ["titulacionPrincipalId"],
        "Datos Profesionales": [
            "lugaresTrabajoIds", "situacionProfesionalId", "especialidadesIds", "estadoId"
        ],
    };

    if (Object.keys(catalogos).length === 0) {
        return <Typography variant="body1">Cargando catálogos...</Typography>;
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                {modo === "crear" ? "Crear Colegiado" : `Editar Colegiado: ${initialValues?.codigo}`}
            </Typography>

            <Formik
                initialValues={valoresTransformados}
                enableReinitialize
                validationSchema={Yup.object({
                    nif: Yup.string().required("Obligatorio").test("dni-valido", "DNI inválido", validarDNI),
                    email: Yup.string().email("Formato incorrecto").required("Obligatorio"),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const endpoint = `${process.env.REACT_APP_API_URL}/api/admin/colegiados/${initialValues.codigo}`;
                        await axios.put(endpoint, values, {
                            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                        });

                        const cambios = {
                            colegiado: { codigo: initialValues.codigo }
                        };

                        for (const key in values) {
                            const nuevo = values[key];
                            const original = originalesTransformados[key];

                            const normalizar = (val) => {
                                if (Array.isArray(val)) return val.map(String).sort().join(",");
                                if (typeof val === "object" && val !== null) return JSON.stringify(val);
                                return String(val ?? "");
                            };

                            if (normalizar(nuevo) !== normalizar(original)) {
                                cambios[key] = nuevo;
                            }
                        }

                        console.log("Cambios detectados:", cambios);

                        if (Object.keys(cambios).length > 1) {
                            await axios.post(`${process.env.REACT_APP_API_URL}/api/cambios/solicitud-cambio-admin`, cambios, {
                                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                            });
                        }

                        setMensaje({ tipo: "success", texto: "Cambios guardados y registrados correctamente." });
                        if (onSubmit) await onSubmit(values);
                    } catch (err) {
                        console.error("Error al guardar:", err);
                        setMensaje({ tipo: "error", texto: "Error al guardar o registrar cambios." });
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, handleChange, handleSubmit, errors, touched }) => (
                    <Form onSubmit={handleSubmit}>
                        {Object.entries(secciones).map(([titulo, camposSeccion]) => (
                            <Box key={titulo} mt={4} mb={2}>
                                <Typography variant="h6" gutterBottom>
                                    {titulo}
                                </Typography>
                                <Grid container spacing={2}>
                                    {camposSeccion.map((campo) => {
                                        const opciones = obtenerOpciones(campo);
                                        const valorActual = values[campo];

                                        return (
                                            <Grid item xs={12} sm={6} key={campo}>
                                                {esCampoSelect(campo) ? (
                                                    <FormControl fullWidth>
                                                        <InputLabel id={`${campo}-label`} shrink>
                                                            {campos[campo]}
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`${campo}-label`}
                                                            name={campo}
                                                            multiple={esMultiple(campo)}
                                                            value={
                                                                esMultiple(campo)
                                                                    ? Array.isArray(valorActual)
                                                                        ? valorActual
                                                                        : []
                                                                    : valorActual || ""
                                                            }
                                                            onChange={handleChange}
                                                            renderValue={(selected) =>
                                                                esMultiple(campo)
                                                                    ? selected
                                                                        .map((id) =>
                                                                            obtenerOpciones(campo)?.find((op) => String(op.id) === String(id))?.nombre
                                                                        )
                                                                        .filter(Boolean)
                                                                        .join(", ")
                                                                    : obtenerOpciones(campo)?.find((op) => String(op.id) === String(selected))?.nombre || ""
                                                            }
                                                        >
                                                            {obtenerOpciones(campo).map((op) => (
                                                                <MenuItem key={op.id} value={String(op.id)}>
                                                                    {op.nombre}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        label={campos[campo]}
                                                        name={campo}
                                                        value={valorActual || ""}
                                                        onChange={handleChange}
                                                        error={Boolean(touched[campo] && errors[campo])}
                                                        helperText={touched[campo] && errors[campo]}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        ))}

                        <Box mt={4} display="flex" gap={2}>
                            <Button type="submit" variant="contained">
                                Guardar
                            </Button>
                        </Box>

                        {mensaje && (
                            <Box mt={2}>
                                <Alert severity={mensaje.tipo}>{mensaje.texto}</Alert>
                            </Box>
                        )}
                    </Form>
                )}
            </Formik>
        </Paper>
    );
};

export default ColegiadoAdminForm;
